import { useMemo } from 'react';
import styled from 'styled-components';

import { AccessModes, Metrics, Sources } from 'components/insights/constants';
import { EM_DASH } from 'components/insights/formatting';
import MetricTile from 'components/insights/layout/tiles/MetricTile';
import DonutChart from 'components/shared/charts/DonutChart';
import { colors } from 'styles/theme/colors';

import useNetworkPerformanceAnalytics from '../query/useNetworkPerformanceAnalytics';

const READMISSION_COLOR = colors.accentRed;

export default function ReadmissionRateOverviewTile() {
  const queryParams = useMemo(
    () => ({
      source: Sources.LOCATION_EPISODE_DISCHARGES,
      accessModes: [AccessModes.MANAGER],
      metrics: [Metrics.READMISSION_RATE, Metrics.READMISSIONS_SUM, Metrics.ID_COUNT],
    }),
    []
  );

  const { loading, data } = useNetworkPerformanceAnalytics(queryParams, {
    queryKey: 'networkPerformanceReadmissionRate',
  });

  const readmissionRate = data.metrics.getCurrentSeries(Metrics.READMISSION_RATE)[0];
  const readmissionsSum = data.metrics.getCurrentSeries(Metrics.READMISSIONS_SUM)[0] ?? 0;
  const dischargesCount = data.metrics.getCurrentSeries(Metrics.ID_COUNT)[0] ?? 0;
  const nonReadmissions = Math.max(dischargesCount - readmissionsSum, 0);

  const hasData = readmissionRate != null;

  const chartData = [
    { name: 'Readmissions', y: readmissionsSum, color: READMISSION_COLOR },
    { name: 'Total Discharges', y: nonReadmissions, color: colors.black10 },
  ];

  const legend = [
    { name: 'Readmissions', value: readmissionsSum, color: READMISSION_COLOR },
    { name: 'Total Discharges', value: dischargesCount, color: colors.black10 },
  ];

  return (
    <MetricTile label='Readmission Rate' showValue={false} loading={loading} hasData={hasData}>
      <Container>
        <ChartWrapper>
          <DonutChart
            data={chartData}
            centerValue={hasData ? `${readmissionRate.toFixed(1)}%` : EM_DASH}
            centerLabel=''
            config={{
              chart: { height: 116, width: 116 },
              title: { y: 16 },
              legend: { enabled: false },
              plotOptions: {
                pie: { size: 116, innerSize: 66, center: ['50%', '50%'] },
              },
            }}
          />
        </ChartWrapper>
        <LegendList>
          {legend.map((item) => (
            <LegendRow key={item.name}>
              <LegendLeft>
                <LegendDot $color={item.color} />
                <LegendName>{item.name}</LegendName>
              </LegendLeft>
              <LegendValue>{item.value}</LegendValue>
            </LegendRow>
          ))}
        </LegendList>
      </Container>
    </MetricTile>
  );
}

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ChartWrapper = styled.div`
  flex-shrink: 0;
  width: 116px;
`;

const LegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LegendLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendName = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.black75};
`;

const LegendValue = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
`;
